/**
 * REEDOS Knowledge Base — release changelog view (`/kb/changelog`).
 *
 * Displays release history with the 4 mandated categories:
 * New, Changed, Deprecated, Removed. Sourced from GET /api/kb/versions.
 */
import { History, Sparkles, Wand2, Trash2, PackageMinus } from "lucide-react";
import { useEffect, useState } from "react";
import { kbListVersions } from "@/lib/kb";

const CATEGORY_ORDER = ["new", "changed", "deprecated", "removed"];
const CATEGORY_META = {
    new:        { label: "New",         Icon: Sparkles,     color: "text-emerald-600" },
    changed:    { label: "Changed",     Icon: Wand2,        color: "text-blue-600" },
    deprecated: { label: "Deprecated",  Icon: PackageMinus, color: "text-amber-600" },
    removed:    { label: "Removed",     Icon: Trash2,       color: "text-red-600" },
};

export default function KBChangelog() {
    const [versions, setVersions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        setLoading(true);
        kbListVersions(100)
            .then((d) => setVersions(d.items || []))
            .catch((err) => setError(err))
            .finally(() => setLoading(false));
    }, []);

    return (
        <div className="max-w-3xl" data-testid="kb-changelog-page">
            <header className="mb-4">
                <h1 className="text-2xl font-semibold flex items-center gap-2">
                    <History className="h-6 w-6 text-primary" />
                    Release Changelog
                </h1>
                <p className="text-muted-foreground text-sm mt-1">
                    New features, changed behaviour, deprecated functionality, and removed capabilities across REEDOS releases.
                </p>
            </header>

            {loading && <div className="text-sm text-muted-foreground">Loading changelog…</div>}
            {error && <div className="text-sm text-destructive">Failed to load changelog.</div>}
            {!loading && !error && versions.length === 0 && (
                <div
                    className="border border-dashed border-border rounded-md p-6 bg-muted/40 text-sm"
                    data-testid="kb-changelog-empty"
                >
                    No release entries yet. Once an operator publishes a version
                    via <code>POST /api/kb/admin/version/publish</code>, it&apos;ll show up here.
                </div>
            )}

            <ul className="space-y-4">
                {versions.map((v) => (
                    <li
                        key={v.semver}
                        className="border border-border rounded-md p-4"
                        data-testid={`kb-changelog-version-${v.semver}`}
                    >
                        <div className="flex items-center justify-between mb-2">
                            <h2 className="text-lg font-semibold">v{v.semver}</h2>
                            <span className="text-xs text-muted-foreground">
                                {v.released_at}
                            </span>
                        </div>
                        <div className="grid gap-3 md:grid-cols-2">
                            {CATEGORY_ORDER.map((cat) => {
                                const items = v.categories?.[cat] || [];
                                if (!items.length) return null;
                                const { label, Icon, color } = CATEGORY_META[cat];
                                return (
                                    <div
                                        key={cat}
                                        data-testid={`kb-changelog-${v.semver}-${cat}`}
                                    >
                                        <div className={`flex items-center gap-1 text-xs uppercase tracking-wider ${color} mb-1`}>
                                            <Icon className="h-3.5 w-3.5" />
                                            {label}
                                        </div>
                                        <ul className="text-sm list-disc list-inside space-y-0.5 text-foreground/90">
                                            {items.map((it, i) => (
                                                <li key={`${cat}-${i}`}>{it}</li>
                                            ))}
                                        </ul>
                                    </div>
                                );
                            })}
                        </div>
                    </li>
                ))}
            </ul>
        </div>
    );
}
